/**
 * batchTaskTracker
 *
 * 跟踪 image2psd 批量生成任务的完成状态。
 * 当所有批量任务完成后，触发 SIG_CONTINUE_STAGE2 信号进入阶段 2（透明化 + OCR + PSD 合成）。
 *
 * 设计：
 * - 模块级 Map 存储 image2psd_task_id → 批量任务状态
 * - 每个 task 完成时（成功/失败）更新计数
 * - 全部完成时自动触发后端阶段 2
 * - 去重保护：防止多次触发同一 batch 的 stage2
 */
import { AgentService } from '@/services/agent';
import { generateApi } from '@/services/generateApi';
import { useAgentStore } from '@/stores/agentStore';
import { useNodeStore } from '@/stores/nodeStore';
import { useTaskStore } from '@/stores/taskStore';
import { createLocalId } from '../tagParser';

// ===== 信号常量（与后端 image2psd_skill.py 保持一致）=====
const SIG_CONTINUE_STAGE2 = '__IMAGE2PSD_CONTINUE_STAGE2__';
const SIGNAL_SEPARATOR = '::';

// ===== 类型定义 =====

interface BatchTaskEntry {
  /** image2psd 任务 ID（用于后端阶段路由） */
  image2psdTaskId: string;
  /** 关联的画布节点 ID（主协调节点，用于 stage2 触发） */
  nodeId: string;
  /**
   * 5.4 修复：批量任务关联的所有画布节点 ID。
   * 每个子任务有独立节点，进度更新需覆盖所有节点。
   * 若未提供则回退到 [nodeId]（兼容旧调用方）。
   */
  allNodeIds: string[];
  /**
   * 🔴 单流到终态（2026-08-29）：task_id → 画布节点 ID 映射。
   * 流式期间子任务完成时 markTaskCompleted 据此读取节点图片（taskResultSync
   * 已写入 data.image），增量回填 activeTask.mediaData.results 实时显示。
   * registerBatch 时按 taskIds[i] ↔ allNodeIds[i] 顺序对齐构建。
   */
  taskNodeMap: Map<string, string>;
  /** 所有批量任务 ID 集合 */
  allTaskIds: Set<string>;
  /** 已完成任务 ID 集合 */
  completedTaskIds: Set<string>;
  /** 已成功任务 ID 集合 */
  succeededTaskIds: Set<string>;
  /**
   * 🔴 会话归属：注册时的会话 ID。批量任务完成时用户可能已切换会话
   * （如切到历史会话浏览），stage2 触发必须落库到归属会话，且 UI
   * 合并仅在用户停留在归属会话时进行，避免跨会话串扰。
   */
  sessionId: string;
  /** 注册时的项目 ID（同上，切换会话可能伴随切换项目） */
  projectId: string;
  /** 是否已触发 stage2（去重保护） */
  stage2Triggered: boolean;
}

// ===== 模块级状态 =====

const _batchMap = new Map<string, BatchTaskEntry>();
/** task_id → image2psd_task_id 反查索引 */
const _taskIndex = new Map<string, string>();

// ===== 🔴 单流到终态（2026-08-29）：活跃流注册 =====
/**
 * image2psd_task_id → 挂载它的活跃 SSE 流会话 ID。
 *
 * 单流模式下批量子任务由协程内等待（_wait_for_batch_tasks）并进程内续跑
 * stage2——批量判满时若 SSE 流仍活跃（本表命中），前端不再发
 * SIG_CONTINUE_STAGE2（避免与协程续跑并发双请求，后端幂等防护兜底但
 * 请求本身冗余）。流结束/出错时由 useStreamLifecycle 清除本表条目，
 * 之后恢复路径（刷新/轮询）判满仍走原降级触发。
 */
const _activeStreamBatches = new Map<string, string>();

/** chunk 级挂载批量任务时注册（batchStreamMounter 调用） */
export function registerActiveStreamBatch(
  image2psdTaskId: string,
  streamSessionId: string,
): void {
  if (!image2psdTaskId || !streamSessionId) return;
  _activeStreamBatches.set(image2psdTaskId, streamSessionId);
}

/** 流结束/出错时清除（useStreamLifecycle 调用），此后判满恢复降级触发 */
export function clearActiveStreamBatch(streamSessionId: string): void {
  for (const [key, sid] of _activeStreamBatches.entries()) {
    if (sid === streamSessionId) _activeStreamBatches.delete(key);
  }
}

/** 批量是否属于仍在推送的活跃流（判满时跳过前端 stage2 触发） */
export function isBatchInActiveStream(image2psdTaskId: string): boolean {
  return _activeStreamBatches.has(image2psdTaskId);
}

// ===== 流内挂载暂存（§4.3 挂起模型）=====
/**
 * 确认段1 预创建的画布节点映射（msgId → elementNodeMap/orderedNodeIds/primaryNodeId）。
 *
 * 挂起模型下批量任务提交结果经 [COLLABORATION_RESULT] 流内送达，由
 * finalizeTextMsg 批量分支挂载——但段1 的预创建产物在 useGenerationConfirm
 * 闭包内，finalize 时不可达。确认时暂存至此，finalize 批量分支消费
 * （优先级：暂存 map → 消息 metadata.canvas_node_map → 兜底新建节点）。
 */
interface StagedCanvasMap {
  elementNodeMap: Map<string, string>;
  orderedNodeIds: string[];
  primaryNodeId: string;
}
const _stagedCanvasMaps = new Map<string, StagedCanvasMap>();

/** 确认段1 画布预创建后暂存（useGenerationConfirm 调用） */
export function stageConfirmCanvasMap(
  msgId: string | undefined,
  entry: StagedCanvasMap,
): void {
  if (!msgId) return;
  _stagedCanvasMaps.set(msgId, entry);
}

/**
 * 读取暂存的画布映射（finalizeTextMsg 批量分支调用）。
 * 不删除条目——网络重试/多 tab 场景重复消费时幂等（taskStore 已含 taskId 跳过）。
 */
export function consumeConfirmCanvasMap(msgId: string | undefined): StagedCanvasMap | null {
  if (!msgId) return null;
  return _stagedCanvasMaps.get(msgId) || null;
}

// ===== 公开 API =====

/**
 * 注册批量任务组。
 *
 * 在 useGenerationConfirm._handleTaskTypeResult 中批量任务提交后调用，
 * 将所有 task_id 关联到同一个 image2psd_task_id。
 *
 * @param image2psdTaskId image2psd 任务 ID
 * @param taskIds 批量任务 ID 列表
 * @param nodeId 主协调画布节点 ID（用于 stage2 触发）
 * @param allNodeIds 5.4：所有子任务的画布节点 ID（进度更新覆盖所有节点）。
 *                   若不传则回退到 [nodeId]，兼容旧调用方。
 * @param ownerSessionId 🔴 归属会话（2026-08-27）：confirm 的 execute 是长 await，
 *                       响应处理时用户可能已切换会话——归属会话必须在发请求前
 *                       捕获并显式传入，否则注册到错误的会话（stage2 落库错位）。
 *                       未传时回退读当前视图（兼容旧调用方）。
 * @param ownerProjectId 归属项目（同上）
 */
export function registerBatch(
  image2psdTaskId: string,
  taskIds: Array<{ task_id: string }>,
  nodeId: string,
  allNodeIds?: string[],
  ownerSessionId?: string,
  ownerProjectId?: string,
): void {
  if (!image2psdTaskId || taskIds.length === 0) return;

  // 🔴 单流到终态（2026-08-29）幂等化：chunk 级挂载与 finalize 兜底可能对同
  // 一批量组先后 registerBatch。原实现无条件覆盖 entry（completedTaskIds
  // 清零）——流式期间已完成的子任务计数被抹掉，判满条件被破坏。已有 entry
  // 直接保留（Set 语义重复注册无副作用）。
  if (_batchMap.has(image2psdTaskId)) {
    console.log(
      `[batchTaskTracker] registerBatch 幂等命中（已有 entry 保留计数）: ${image2psdTaskId}`,
    );
    return;
  }

  const allTaskIdSet = new Set(taskIds.map((t) => t.task_id));
  // 🔴 会话归属：优先使用显式传入的归属会话/项目（发请求前捕获）；
  // 回退读当前视图（原行为，兼容恢复路径与旧调用方——恢复路径注册时
  // 用户正停留归属会话，视图会话即归属）
  const ownerStore = useAgentStore.getState();
  // 🔴 单流到终态：task→node 映射（顺序对齐时构建；restore 路径 allNodeIds
  // 单元素不对齐时留空 Map，markTaskCompleted 图片回填自动跳过）
  const nodeIds = (allNodeIds && allNodeIds.length > 0) ? [...allNodeIds] : [nodeId];
  const taskNodeMap = new Map<string, string>();
  if (nodeIds.length === taskIds.length) {
    taskIds.forEach((t, i) => {
      if (t.task_id && nodeIds[i]) taskNodeMap.set(t.task_id, nodeIds[i]);
    });
  }
  _batchMap.set(image2psdTaskId, {
    image2psdTaskId,
    nodeId,
    // 5.4：记录所有节点 ID，进度更新时遍历；未提供时回退到主协调节点
    allNodeIds: nodeIds,
    taskNodeMap,
    allTaskIds: allTaskIdSet,
    completedTaskIds: new Set(),
    succeededTaskIds: new Set(),
    sessionId: ownerSessionId || ownerStore.sessionId || '',
    projectId: ownerProjectId || ownerStore.projectId || '',
    stage2Triggered: false,
  });

  // 建立反查索引
  for (const tid of allTaskIdSet) {
    _taskIndex.set(tid, image2psdTaskId);
  }

  console.log(
    `[batchTaskTracker] 注册批量任务: image2psd_task_id=${image2psdTaskId}, ` +
      `task_count=${allTaskIdSet.size}, nodeId=${nodeId}, allNodeIds_count=${allNodeIds?.length || 1}`,
  );
}

/**
 * 标记批量任务中的一个任务已完成。
 *
 * 在 taskResultSync.applyTaskResultToCanvasAndMessage 中调用，
 * 每个任务完成（成功/失败）后更新计数。
 * 当所有任务完成时自动触发 SIG_CONTINUE_STAGE2。
 *
 * @param taskId 完成的任务 ID
 * @param success 是否成功
 * @returns 如果触发了 stage2 则返回 image2psdTaskId，否则返回 null
 */
export function markTaskCompleted(
  taskId: string,
  success: boolean,
): string | null {
  const image2psdTaskId = _taskIndex.get(taskId);
  if (!image2psdTaskId) return null;

  const entry = _batchMap.get(image2psdTaskId);
  if (!entry || entry.stage2Triggered) return null;

  entry.completedTaskIds.add(taskId);
  if (success) {
    entry.succeededTaskIds.add(taskId);
  }

  // 🔴 5.4 修复：更新所有批量任务关联的画布节点进度，而非仅更新主协调节点。
  //   每个子任务有独立节点，用户需要在每个节点上看到"3/5 完成"的进度提示。
  const _completedCount = entry.completedTaskIds.size;
  const _totalCount = entry.allTaskIds.size;
  const _succeededCount = entry.succeededTaskIds.size;
  const _progressText = `${_succeededCount}/${_totalCount} 完成${_completedCount < _totalCount ? '，等待中...' : ''}`;
  try {
    const nodeStore = useNodeStore.getState();
    for (const nid of entry.allNodeIds) {
      nodeStore.updateNode(nid, { progress: _progressText });
    }
  } catch (e) {
    // 节点可能已被删除，忽略
  }

  // 🔴 单流到终态：流式实时表格——activeTask.mediaData 批量增量更新：
  //   1. batchItems 逐项状态翻转（RUNNING → SUCCESS/FAILED）
  //   2. results 图片实时回填——成功子任务从画布节点（taskResultSync 先于本函数
  //      已写入 data.image）读取图片 URL，按 allTaskIds 顺序对齐写入（MediaResultBlock
  //      按 index 对齐 results[idx]↔batchItems[idx]），流式期间即可见图片网格
  //（消息尚未创建，taskResultSync 的消息回填不可达，此处是唯一实时通道）
  // 🔴 会话切换防护：子任务完成可能发生在用户切走后（方案C stage 容器持流）。
  // 视图 activeTask 命中（用户停留归属会话）→ 正常视图更新；未命中 → 写
  // 容器快照（updateLiveActiveTask），切回换指针后进度/图片立即可见，
  // 不再等 finalize 重建。
  try {
    const store = useAgentStore.getState();
    // 单条 mediaData 增量计算（视图/容器共用）：终态翻转 + 节点图片按序回填
    const buildUpdatedMedia = (atMedia: any): any | null => {
      const items = atMedia?.batchItems;
      if (!Array.isArray(items) || !items.some((bi: any) => bi.taskId === taskId)) return null;
      // 图片收集：节点图 → 按批量顺序槽位对齐
      const order = Array.from(entry.allTaskIds);
      const results: Array<{ url: string } | undefined> =
        Array.isArray(atMedia?.results) ? [...atMedia.results] : new Array(order.length);
      const nid = entry.taskNodeMap.get(taskId);
      if (success && nid) {
        const node = useNodeStore.getState().getNodeById(nid) as any;
        const url = node?.data?.image || node?.image;
        if (url) results[order.indexOf(taskId)] = { url };
      }
      return {
        ...atMedia,
        ...(results.some(Boolean) ? { results } : {}),
        batchItems: items.map((bi: any) =>
          bi.taskId === taskId ? { ...bi, status: success ? 'SUCCESS' : 'FAILED' } : bi,
        ),
      };
    };
    const viewMedia = buildUpdatedMedia(store.activeTask?.mediaData);
    if (viewMedia) {
      store.updateActiveTask({ mediaData: viewMedia });
    } else {
      const liveMedia = buildUpdatedMedia(
        store.getSessionLiveState(entry.sessionId)?.activeTask?.mediaData,
      );
      if (liveMedia) {
        store.updateLiveActiveTask(entry.sessionId, { mediaData: liveMedia });
      }
    }
  } catch (e) {
    // activeTask 可能已被清除/切换，忽略
  }

  const allDone = _completedCount >= _totalCount;
  if (!allDone) return null;

  // 🔴 单流到终态（2026-08-29）：批量属于仍活跃的 SSE 流时跳过前端触发——
  // 后端协程内等待已就绪，stage2 由进程内续跑（_wait_for_batch_tasks 判满
  // 后 continue 信号），前端再发 SIG_CONTINUE_STAGE2 属冗余请求（后端
  // _stage2_entry 幂等防护兜底，但双请求竞态仍浪费）。流结束/出错后
  // _activeStreamBatches 清除，恢复路径判满仍走降级触发。
  if (isBatchInActiveStream(image2psdTaskId)) {
    entry.stage2Triggered = true;
    console.log(
      `[batchTaskTracker] 批量任务全部完成（活跃流内，stage2 由后端协程续跑，前端跳过触发）: ` +
        `image2psd_task_id=${image2psdTaskId}`,
    );
    return image2psdTaskId;
  }

  // 全部完成 → 触发 stage2
  entry.stage2Triggered = true;
  const succeededCount = entry.succeededTaskIds.size;
  const totalCount = entry.allTaskIds.size;

  console.log(
    `[batchTaskTracker] 批量任务全部完成: image2psd_task_id=${image2psdTaskId}, ` +
      `succeeded=${succeededCount}/${totalCount}, 触发 SIG_CONTINUE_STAGE2`,
  );

  // 异步触发，不阻塞调用方
  void _triggerStage2(image2psdTaskId, entry.nodeId, succeededCount, totalCount).catch(
    (err) => {
      console.error('[batchTaskTracker] 触发 stage2 失败:', err);
    },
  );

  return image2psdTaskId;
}

/**
 * 清理已完成的批量任务条目。
 *
 * 在 stage2 触发后或任务取消时调用，释放内存。
 */
export function clearBatch(image2psdTaskId: string): void {
  const entry = _batchMap.get(image2psdTaskId);
  if (!entry) return;
  for (const tid of entry.allTaskIds) {
    _taskIndex.delete(tid);
  }
  _batchMap.delete(image2psdTaskId);
}

/**
 * 查询 taskId 是否属于某个批量任务组。
 */
export function isBatchTask(taskId: string): boolean {
  return _taskIndex.has(taskId);
}

/**
 * 🔴 单流到终态（2026-08-29）：查询批量组内每个子任务的实时完成状态。
 *
 * 单流模式下批量子任务在流式期间完成（消息尚未创建，taskResultSync 的
 * batchItems 更新丢失），finalize 构造 mediaData 时用本函数重建准确的
 * 逐元素终态（成功/失败/仍在运行）。
 */
export function getBatchTaskStates(
  image2psdTaskId: string,
): Map<string, string> | null {
  const entry = _batchMap.get(image2psdTaskId);
  if (!entry) return null;
  const states = new Map<string, string>();
  for (const tid of entry.allTaskIds) {
    if (entry.succeededTaskIds.has(tid)) states.set(tid, 'SUCCESS');
    else if (entry.completedTaskIds.has(tid)) states.set(tid, 'FAILED');
    else states.set(tid, 'RUNNING');
  }
  return states;
}

/**
 * 🔴 修复（2026-08-27）：恢复路径重建批量跟踪。
 *
 * _batchMap/_taskIndex 是模块级内存态，页面刷新/新窗口打开后清空，导致：
 * - 子任务完成（WS/轮询检测）时 markTaskCompleted 查不到注册 → 计数永远
 *   不满 → stage2（PSD 合成）永不触发，流水线静默中断；
 * - isBatchTask 返回 false → taskResultSync 把批量消息 taskStatus 提前写成
 *   COMPLETED，批量任务卡错误收尾。
 *
 * 恢复数据源：会话消息 metadata（batchTaskIds / 后端 task_ids +
 * image2psd_task_id + pendingNodeId，均在 confirm 提交批次时持久化到 DB）。
 * 由 loadSessionMessages → restorePendingMediaTasks 在加载归属会话时调用：
 * - 已完成的子任务 → 立即 markTaskCompleted 计数（若全部完成则直接触发
 *   stage2，覆盖"切走期间图片已生成完"的场景）；
 * - 运行中的子任务 → 注册回 taskStore（WS final 态事件需 pendingTasks 命中
 *   才 emit AGENT_TASK_COMPLETED → taskResultSync → markTaskCompleted）。
 *
 * @param m 后端原始消息（含 metadata）
 * @param sessionId 归属会话 ID（恢复时用户正停留该会话，作为注册归属）
 */
export function restoreBatchFromMessage(m: any, sessionId?: string): void {
  const image2psdTaskId: string | undefined = m.metadata?.image2psd_task_id;
  // 兼容两份持久化来源：FE 写入的 batchTaskIds（string[]）与后端响应
  // metadata 持久化的 task_ids（Array<{task_id}>）
  const rawIds: unknown = m.metadata?.batchTaskIds;
  const ids: string[] = Array.isArray(rawIds)
    ? (rawIds as string[])
    : Array.isArray(m.metadata?.task_ids)
      ? (m.metadata.task_ids as Array<{ task_id?: string }>)
          .map((t) => t?.task_id)
          .filter((tid): tid is string => !!tid)
      : [];
  if (!image2psdTaskId || ids.length === 0) return;
  // 同窗口重复恢复保护（正常 confirm 流程已注册过）
  if (_batchMap.has(image2psdTaskId)) return;

  const nodeId: string = m.metadata?.pendingNodeId || '';
  registerBatch(
    image2psdTaskId,
    ids.map((tid) => ({ task_id: tid })),
    nodeId,
    [nodeId],
    sessionId,
  );
  console.log(
    `[batchTaskTracker] 恢复批量跟踪: image2psd_task_id=${image2psdTaskId}, ` +
      `task_count=${ids.length}, sessionId=${sessionId || '(未指定)'}`,
  );
  void _resumeBatchChildTasks(ids, nodeId).catch((err) => {
    console.warn('[batchTaskTracker] 恢复批量子任务状态失败:', err);
  });
}

/**
 * 逐个查询子任务当前状态：
 * - 终态（success/failed）→ markTaskCompleted 补计数（全部完成即触发 stage2）；
 * - 运行中 → addTask 注册回 taskStore 供 WS final 态匹配。
 * 串行查询避免多请求并发（后端批量生成期间并发 HTTP 曾引发 ReadError）。
 */
async function _resumeBatchChildTasks(taskIds: string[], nodeId: string): Promise<void> {
  for (const tid of taskIds) {
    if (useTaskStore.getState().pendingTasks.has(tid)) continue;
    try {
      const st = await generateApi.getTaskStatus(tid);
      if (st.status === 'success' || st.status === 'failed' || st.status === 'cancelled') {
        markTaskCompleted(tid, st.status === 'success');
      } else {
        // pending / running：注册回 taskStore，WS final 态事件命中后走正常计数
        useTaskStore.getState().addTask(tid, nodeId);
      }
    } catch (e) {
      // 单个子任务状态查询失败不阻断其余恢复
      console.warn(`[batchTaskTracker] 查询子任务 ${tid} 状态失败:`, e);
    }
  }
}

// ===== 内部实现 =====

/**
 * 发送 SIG_CONTINUE_STAGE2 信号到后端，触发阶段 2 处理（PSD 合成）。
 *
 * 后端 Image2PsdSkill 收到信号后：
 * 1. 下载所有生成图
 * 2. 几何校验
 * 3. 透明化 + OCR + PSD 合成
 * 4. 返回结果（immediate 文本或 confirm 弹窗）
 *
 * 🔴 同一技能执行只保留一条消息：批量生图与 PSD 合成结果合并到同一条批量消息
 * （通过 metadata.image2psd_task_id / batchTaskIds 定位），不再另起一条独立回复。
 */
async function _triggerStage2(
  image2psdTaskId: string,
  nodeId: string,
  succeededCount: number,
  totalCount: number,
): Promise<void> {
  // 🔴 跨窗口去重锁（2026-08-27）：A/B 双窗口可能各自通过恢复路径重建批量
  // 注册并同时判满触发 stage2（_batchMap 是窗口内存态，互不可见）。
  // localStorage 同源窗口共享，用锁防止 PSD 合成被执行两次；触发失败时
  // 释放锁允许后续恢复重试。localStorage 不可用时降级为无锁（接受偶发重复）。
  const stage2LockKey = `bongai_i2p_stage2_${image2psdTaskId}`;
  let stage2LockedByUs = false;
  try {
    if (localStorage.getItem(stage2LockKey)) {
      console.log(`[batchTaskTracker] stage2 已由其他窗口触发，本窗口跳过: ${image2psdTaskId}`);
      return;
    }
    localStorage.setItem(stage2LockKey, String(Date.now()));
    stage2LockedByUs = true;
  } catch { /* localStorage 不可用，跳过锁 */ }

  const signal = `${SIG_CONTINUE_STAGE2}${SIGNAL_SEPARATOR}${image2psdTaskId}`;
  const store = useAgentStore.getState();
  const entry = _batchMap.get(image2psdTaskId);
  // 🔴 会话归属：批量任务完成时用户可能已切换会话（如切到历史会话浏览）。
  // signal 必须以注册时的归属会话/项目执行，后端把 stage2 结果落库到正确会话，
  // 避免 A 会话的任务结果串到 B 会话；UI 合并仅在用户停留在归属会话时进行，
  // 否则结果静默落库，切回归属会话时由历史恢复展示。
  const ownerSessionId = entry?.sessionId || '';
  const projectId = entry?.projectId || store.projectId || '';
  const sessionId = ownerSessionId || store.sessionId || undefined;
  const isOwnerActive = !!ownerSessionId && store.sessionId === ownerSessionId;
  const modelId = store.currentModelId || undefined;

  // 定位批量生图消息：优先按 image2psd_task_id 精确匹配，兜底按 batchTaskIds 交集匹配
  const findBatchMsgId = (): string | undefined => {
    const msgs = useAgentStore.getState().messages;
    const bySkillId = msgs.find((m) => m.metadata?.image2psd_task_id === image2psdTaskId);
    if (bySkillId?.id) return bySkillId.id;
    if (entry) {
      const fallback = msgs.find((m) => {
        const ids = m.metadata?.batchTaskIds;
        return Array.isArray(ids) && (ids as string[]).some((t) => entry.allTaskIds.has(t));
      });
      if (fallback?.id) return fallback.id;
    }
    return undefined;
  };

  const batchMsgId = isOwnerActive ? findBatchMsgId() : undefined;

  // 阶段标记：批量消息气泡内显示"正在合成 PSD"+思考读秒，并隐藏评价按钮
  //（仅归属会话激活时；用户在其他会话浏览时不污染其 UI）
  if (isOwnerActive && batchMsgId) {
    store.setMessages((prev) =>
      prev.map((m) =>
        m.id === batchMsgId
          ? { ...m, metadata: { ...m.metadata, stage: 'stage2_transition' } }
          : m,
      ),
    );
  }

  // PSD 合成（下载图片->透明化->OCR->合成）为长耗时同步请求，期间无 SSE 流式输出：
  // 启动"思考中"读秒（有批量消息时在其气泡内展示等待状态）
  if (isOwnerActive) {
    store.setIsThinking(true);
    store.setThinkingSeconds(0);
    store.startThinkingTimer();
  }

  // 将合成结果合并回批量消息并持久化（psdResult 随 mediaData 序列化到后端 content，历史恢复一致）
  const mergeIntoBatchMsg = (patch: {
    psdResult: string;
    status?: 'SUCCESS' | 'FAILED';
    stage: string;
    generationOptions?: any;
    extraMetadata?: Record<string, any>;
  }): boolean => {
    if (!batchMsgId) return false;
    store.setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== batchMsgId) return m;
        return {
          ...m,
          ...(patch.generationOptions !== undefined ? { generationOptions: patch.generationOptions } : {}),
          mediaData: m.mediaData
            ? {
                ...m.mediaData,
                psdResult: patch.psdResult,
                ...(patch.status ? { status: patch.status } : {}),
              }
            : m.mediaData,
          metadata: { ...m.metadata, ...(patch.extraMetadata || {}), stage: patch.stage },
        };
      }),
    );
    // 持久化：mediaData 序列化回写后端 content，metadata（psd_url/preview_url/stage
    // 等）同步合并到后端，刷新后历史消息仍能渲染 PSD 下载链接与预览图
    const finalMsg = useAgentStore.getState().messages.find((m) => m.id === batchMsgId);
    if (finalMsg?.id && finalMsg.mediaData && sessionId) {
      void AgentService.updateMessageMetadata(
        finalMsg.id,
        sessionId,
        { ...(patch.extraMetadata || {}), stage: patch.stage },
        {
          content: JSON.stringify(finalMsg.mediaData),
          msg_type: finalMsg.type || 'image',
        },
      ).catch((err) => {
        console.warn('[batchTaskTracker] 同步 PSD 合成结果到后端失败:', err);
      });
    }
    return true;
  };

  // 兜底：找不到批量消息时保留旧行为（独立过渡消息，完成后替换为结果）
  //（仅归属会话激活时插入，避免过渡消息出现在用户正在浏览的其他会话）
  const transitionMsgId = createLocalId();
  if (isOwnerActive && !batchMsgId) {
    store.setMessages((prev) => [
      ...prev,
      {
        id: transitionMsgId,
        role: 'model' as const,
        type: 'text' as const,
        text: `✅ 批量生成完成（${succeededCount}/${totalCount} 张成功），正在进入 PSD 合成阶段...`,
        metadata: {
          image2psd_task_id: image2psdTaskId,
          stage: 'stage2_transition',
        },
      },
    ]);
  }

  try {
    const result = await AgentService.execute({
      project_id: projectId,
      message: signal,
      model_id: modelId,
      session_id: sessionId,
    });

    if (result.agentId && isOwnerActive) {
      store.setCurrentAgentId(result.agentId);
    }

    // 非归属会话激活（用户已切走）：结果已由后端落库到归属会话，
    // 不做任何 UI 更新，切回归属会话时历史恢复展示
    if (!isOwnerActive) return;
    // await 期间用户可能刚切换会话：再次校验，防止 merge 把归属会话
    // 消息写入切换后的会话列表/本地缓存
    if (ownerSessionId && useAgentStore.getState().sessionId !== ownerSessionId) return;

    // 注意：AgentExecuteResponse 类型未声明 metadata 字段，但后端 execution_service.py 会返回
    const responseType = result.type || 'immediate';
    const content = result.content || '';
    const resultMetadata = (result as any).metadata || {};
    const isConfirm = responseType === 'confirm';

    if (batchMsgId) {
      // ✅ 合并路径：结果写入批量生图消息
      // confirm（几何校验失败）→ generationOptions 触发确认弹窗；immediate → 直接展示结果文本
      const items = useAgentStore.getState().messages.find((m) => m.id === batchMsgId)
        ?.mediaData?.batchItems;
      const batchFinalStatus =
        Array.isArray(items) && items.some((bi) => bi.status === 'FAILED') ? 'FAILED' : 'SUCCESS';
      const merged = mergeIntoBatchMsg({
        psdResult: content || 'PSD 合成阶段已完成',
        status: isConfirm ? batchFinalStatus : 'SUCCESS',
        stage: isConfirm ? 'stage2_confirm' : 'stage2_result',
        ...(isConfirm ? { generationOptions: result.generationOptions } : {}),
        extraMetadata: resultMetadata,
      });
      if (merged) return;
    }

    // 兜底路径：替换独立过渡消息
    store.setMessages((prev) =>
      prev.map((m) =>
        m.id === transitionMsgId
          ? {
              ...m,
              type: isConfirm ? 'generation_confirm' : 'text',
              text: content || 'PSD 合成阶段已完成',
              generationOptions: result.generationOptions,
              metadata: {
                ...m.metadata,
                ...resultMetadata,
                stage: 'stage2_result',
              },
            }
          : m,
      ),
    );
    // 如果后端返回 workflow_wait（需要用户交互），由现有的弹窗机制处理
    // 如果返回 confirm（几何校验失败），generationOptions 已设置，由 GenerationConfirmSheet 处理
  } catch (err: any) {
    const errMsg = err.message || '未知错误';
    console.error('[batchTaskTracker] stage2 触发失败:', err);
    // 🔴 跨窗口锁释放：触发失败（网络/后端异常）时解锁，允许下次会话恢复重试
    if (stage2LockedByUs) {
      try { localStorage.removeItem(stage2LockKey); } catch { /* 忽略 */ }
    }
    // 非归属会话：仅记录错误，不打扰用户正在浏览的会话
    //（归属会话内无过渡消息/批量消息可更新，切回时无残留 UI）
    if (!isOwnerActive) return;
    if (batchMsgId) {
      const merged = mergeIntoBatchMsg({
        psdResult: `❌ PSD 合成失败：${errMsg}`,
        status: 'FAILED',
        stage: 'stage2_error',
      });
      if (merged) return;
    }
    // 兜底路径：更新独立过渡消息为错误状态
    store.setMessages((prev) =>
      prev.map((m) =>
        m.id === transitionMsgId
          ? {
              ...m,
              type: 'error' as const,
              text: `PSD 合成阶段启动失败：${errMsg}`,
              metadata: {
                ...m.metadata,
                stage: 'stage2_error',
              },
            }
          : m,
      ),
    );
  } finally {
    // 合成结束（成功/失败）停止"思考中"读秒
    store.clearThinkingTimer();
    store.setIsThinking(false);
    // 清理批量任务跟踪状态
    clearBatch(image2psdTaskId);
  }
}
